import {
	Component,
	Input,
	ElementRef,
	ViewChild,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Output,
	EventEmitter,
	AfterViewInit,
	SimpleChanges,
	HostListener,
	OnInit,
	OnDestroy,
	OnChanges
} from '@angular/core';
import { Subscription, Subject, combineLatest } from 'rxjs';

import { ReduxService } from '../../../../../services/redux.service';

import { InteractivityEditorService } from '../../services/interactivity-editor.service';
import { InteractivityMediaService } from '../../services/interactivity-media.service';
import { InteractivityWidgetSelectionService } from '../../services/interactivity-widget-selection.service';
import { InteractivityWidgetDefinitionService } from '../../services/interactivity-widget-definition.service';
import { InteractivityWidgetService } from '../../services/interactivity-widget.service';
import { IWidget } from '../../interfaces/IWidget';
import { IInOutPoint } from '../../redux/timeline-data.model';
import { TimelineDataActions } from '../../redux/timeline-data-actions';
import { Track } from '../../classes/Track';
import { IWidgetSelection, IInCurrentTimeCollection } from '../../interfaces/IWidgetSelection';
import { UntypedFormControl } from '@angular/forms';
import { ITrackAreaItem } from '../../interfaces/ITrackAreaItem';
import { TrackGroup } from '../../classes/TrackGroup';
import { BbDragEvent } from '../../../../../directives/bb-drag.directive';
import { InteractivityStateService } from '../../services/interactivity-state.service';
import { modKeyDown } from '../../../../../shared/utils/mod-key-down.util';
import { TimepointConfiguration, FlatITrackAreaItem } from './tracks.component';
import { IWidgetProperty } from '../../interfaces/IWidgetProperty';
import { debounceTime } from 'rxjs/operators';
import { WidgetType } from '../../enums/WidgetType';
import { StageWidget } from '../../classes/StageWidget';
import { BbResizeEvent } from 'app/directives/bb-resize.directive';

export const BACKGROUND_COLOR = '#424242';
export const IE_NEUTRAL = '#979797';

@Component({
	selector: 'track',
	templateUrl: './track.component.html',
	styleUrls: ['./track.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})

export class TrackComponent implements AfterViewInit, OnInit, OnDestroy, OnChanges {

	@ViewChild('labelInput') inputRef: ElementRef;
	@ViewChild('backgroundCanvas') backgroundCanvas: ElementRef;
	@ViewChild('trackElementContainer') trackElementContainer: ElementRef;

	@Input() track: Track;
	@Input() tracks: FlatITrackAreaItem[];
	@Input() type: string;
	@Input() index: string;
	@Input() hiddenTracks: string[];
	@Input() selectedTrackIds: any[];
	@Output() onEditTrack: EventEmitter<any> = new EventEmitter<any>();
	@Output() trackElementContextMenu: EventEmitter<any> = new EventEmitter<any>();

	animateConfig: TimepointConfiguration[] = [];
	currentlyInTime = false;
	visibleStageWidgets: StageWidget[];
	stageWidgets: StageWidget[];
	snappingTargets: any[] = [];
	useSnapping = true;
	dragDisabled = false;
	hoveredWidgetIds: string[] = [];
	widgetInClosedGroupHovered = false;
	endscreenEnabled = true;

	public widget: IWidget;
	public canvasVisible = true;
	public trackElementDragging = false;
	public groupHovered = false;
	public trackCollapsed = true;
	public timepointDragging = false;
	public leftData: number;
	public rightData: number;

	private appTrack: ITrackAreaItem = null;
	private appTrackMinInpoint = 0;
	private appTrackMaxOutpoint = 0;
	private labelControl: UntypedFormControl;
	private previousRightData: number;
	private hideRightData: number;
	private duration = 300; // default clip duration
	private subscriptions: Subscription;
	private durationSubject: Subject<number>;
	private zoomFactor: number;

	private dragDisabled_def = false;
	private isDestroyed = false;
	private previousLeftData: number;
	private stateChanges = new Subject<void>();
	// private pattern = '/resources/img/interactivity/ignore-show-hide-texture.png';

	private _groupCollapsed: boolean;
	set groupCollapsed(val: boolean) {
		this._groupCollapsed = val;
		const collapsedGroups = this.interactivityStateService.getPropState('collapsedGroups') || {};
		if (val) {
			collapsedGroups[this.track.id] = this._groupCollapsed;
		} else {
			delete collapsedGroups[this.track.id];
		}
		this.interactivityStateService.setPropState('collapsedGroups', collapsedGroups);

	}
	get groupCollapsed(): boolean {
		return this._groupCollapsed;
	}

	constructor(
		private el: ElementRef,
		public interactivityEditorService: InteractivityEditorService,
		private widgetSelectionService: InteractivityWidgetSelectionService,
		private interactivityWidgetService: InteractivityWidgetService,
		private interactivityWidgetDefinitionService: InteractivityWidgetDefinitionService,
		private interactivityStateService: InteractivityStateService,
		private reduxService: ReduxService,
		private interactivityMediaService: InteractivityMediaService,
		private changeDetector: ChangeDetectorRef) {

		this.durationSubject = new Subject<number>();
		this.labelControl = new UntypedFormControl();
		this.subscriptions = new Subscription();
		this.dragDisabled_def = this.interactivityEditorService
			&& (this.interactivityEditorService.parentEntityType === 'App' || this.interactivityEditorService.parentEntityType === 'Project');
		this.dragDisabled = this.dragDisabled_def;
	}

	@HostListener('window:resize', ['$event'])
	onWindowResize() {
		this.canvasVisible = false;
		setTimeout(() => {
			this.canvasVisible = true;
			this.interactiveShowHide();
			this.changeDetector.markForCheck();
		}, 250);
	}

	ngOnInit() {
		this.stateChanges.pipe(debounceTime(25))
			.subscribe(() => {
				if (!this.isDestroyed) {
					this.changeDetector.detectChanges();
				}
			});
		this.track.elRef = this.el.nativeElement;
		this.subscriptions.add(this.interactivityStateService.propSub('collapsedGroups').subscribe((collapsedGroups: any) => {
			this._groupCollapsed = !!collapsedGroups[this.track.id];
		}));

		this.subscriptions.add(this.interactivityEditorService.widgetVisibilitySubject.subscribe(() => {
			this.update();
			this.stateChanges.next();
		}));
		this.subscriptions.add(
			this.widgetSelectionService.widgetSelectionSubject.subscribe((selection: IWidgetSelection) => {
				let widgetFound = false;
				selection.widgets.forEach((widget: IWidget) => {
					if (widget && this.widget && widget.id === this.widget.id) {
						widgetFound = true;
					}
				});
				if (this._groupCollapsed && this.track && this.track instanceof TrackGroup && this.track.tracks) {
					const widgetIds = this.track.tracks.map(track => track.widgetId);
					let widgetInGroup = false;
					selection.widgets.forEach(widget => {
						if (widget && widget.id && widgetIds.indexOf(widget.id) !== -1) {
							widgetInGroup = true;
						}
					});

					if (widgetInGroup && selection.origin && selection.origin.area !== 'tracks') {
						this.groupCollapsed = false;
					}
				}
				if (this.widget) {
					this.widget.isSelected = widgetFound;
					this.stateChanges.next();
				}
			})
		);
		this.subscriptions.add(this.interactivityEditorService.trackZoomSubject.subscribe((trackZoom: any) => {
			if (trackZoom && trackZoom.zoomFactor && trackZoom.zoomFactor !== this.zoomFactor) {
				this.zoomFactor = trackZoom.zoomFactor;
				this.interactiveShowHide();
			}
		}));

		this.subscriptions.add(
			this.interactivityEditorService.widgetsSubject.subscribe((widgets: IWidget[]) => {
				if (widgets.length > 0) {
					widgets.map((widget: IWidget) => {
						if (widget && this.widget && widget.id === this.widget.id) {
							(this.widget as any)._data.label = (widget as any)._data.label;
						}
					});
					if (this.widget) {
						this.labelControl.setValue((this.widget as any)._data.label || (this.widget as any)._data.widgetdefinitiontype);
						this.update();
					}
				}
			})
		);

		this.subscriptions.add(
			combineLatest([this.interactivityEditorService.tracksSubject, this.interactivityWidgetDefinitionService.widgetDefinitionsSubject]).subscribe(
				(subjects: any[]) => {
					const tracks: ITrackAreaItem[] = subjects[0];
					tracks
						.filter((track: ITrackAreaItem) => this.widget && track instanceof Track && track.widgetId && track.widgetId === this.widget.id)
						.map((trackAreaItem: ITrackAreaItem) => {
							const currentInOutPoints: IInOutPoint[] = this.track && this.track.inOutPoints;
							const track: Track = trackAreaItem as Track;
							if (currentInOutPoints) {
								currentInOutPoints.map((currentInOutPoint: IInOutPoint) => {
									track.inOutPoints.map((inOutPoint: IInOutPoint) => {
										if (currentInOutPoint.id === inOutPoint.id) {
											currentInOutPoint.inpoint = inOutPoint.inpoint;
											currentInOutPoint.outpoint = inOutPoint.outpoint;
											this.interactiveShowHide();
											this.stateChanges.next();
										}
									});
								});
							}
						});
					this.update();
				}
			)
		);

		this.subscriptions.add(
			this.interactivityEditorService.durationSubject.subscribe(
				(duration: number) => {
					this.duration = duration;
					this.stateChanges.next();
					this.durationSubject.next(this.duration);
				})
		);

		this.subscriptions.add(
			this.interactivityMediaService.currentTimeSubject.
				pipe(
					debounceTime(100)
				).subscribe(
					(currentTime: number) => {
						if (this.widget && this.widget.animations && this.widget.animations.length) {
							this.update();
							// when we have animations changing the current time can result in a keyframe being active
							this.stateChanges.next();
						}
					})
		);

		this.subscriptions.add(
			this.widgetSelectionService.hoveredWidgetsSubject.subscribe((hoveredCollection: { widgets: string[] }) => {
				this.hoveredWidgetIds = hoveredCollection.widgets;
				let widgetInClosedGroupHovered = false;

				if (this.track && this.track instanceof TrackGroup && this.track.tracks && this.groupCollapsed) {
					this.track.tracks.forEach((trackInGroup: ITrackAreaItem) => {
						widgetInClosedGroupHovered = widgetInClosedGroupHovered || this.hoveredWidgetIds.indexOf(trackInGroup.widgetId) !== -1;
					});
				}
				this.widgetInClosedGroupHovered = widgetInClosedGroupHovered;
				this.stateChanges.next();
			})
		);

		this.subscriptions.add(
			this.widgetSelectionService.currentlyInTimeSubject.subscribe((currentlyInTimeCollection: IInCurrentTimeCollection) => {
				const currentlyInTime = this.track && currentlyInTimeCollection && currentlyInTimeCollection.trackIds
					&& currentlyInTimeCollection.trackIds.indexOf(this.track.id) !== -1;
				if (currentlyInTime !== this.currentlyInTime) {
					this.currentlyInTime = currentlyInTime;
					this.stateChanges.next();
				}
			})
		);

		this.update();
	}

	toggleWidgetVisibility(widget: StageWidget) {
		this.interactivityEditorService.toggleWidgetVisibility(widget);
	}

	toggleGroupVisibility(trackGroup: TrackGroup) {
		this.interactivityEditorService.toggleWidgetVisibilityMulti(this.stageWidgets, this.visibleStageWidgets.length !== this.stageWidgets.length);
	}

	groupCollapseMouseEvent(ev: MouseEvent) {
		ev.stopPropagation();
		ev.preventDefault();
	}

	groupCollapseClick(ev: MouseEvent) {
		this.groupCollapsed = !this.groupCollapsed;
		ev.stopImmediatePropagation();
		ev.stopPropagation();
		ev.preventDefault();
	}

	trackCollapseClick(ev: MouseEvent) {
		if (this.interactivityEditorService.parentEntityType === 'App') {
			return;
		}
		this.trackCollapsed = !this.trackCollapsed;
		ev.stopPropagation();
		ev.preventDefault();
		this.onEditTrack.emit(!this.trackCollapsed);
	}

	trackByForTimepointTrack(index: number) {
		return index;
	}

	trackByForTrack(index: number) {
		return index;
	}

	ngOnChanges(simpleChanges: SimpleChanges) {
		if (simpleChanges.track && !simpleChanges.track.firstChange || simpleChanges.index && !simpleChanges.index.firstChange ||
			simpleChanges.selectedTrackIds && !simpleChanges.selectedTrackIds.firstChange) {
			this.update();
		}
	}

	update() {
		if (this.track && this.track.widgetId && this.track instanceof Track) { // normal track
			this.appTrack = (this.track.fromAppTrackId) ? this.interactivityEditorService.getTrackById(this.track.fromAppTrackId) : null; // null if not found
			if (this.appTrack && this.appTrack.inOutPoints) {
				const inpoints = this.appTrack.inOutPoints.map((iop: IInOutPoint) => iop && '' + iop.inpoint);
				const outpoints = this.appTrack.inOutPoints.map((iop: IInOutPoint) => iop && '' + iop.outpoint);
				const minInpoint = inpoints.reduce((acc: string, cur: string) => (parseFloat(cur) < parseFloat(acc) ? cur : acc), inpoints[0]);
				const maxOutpoint = outpoints.reduce((acc: string, cur: string) => (parseFloat(cur) > parseFloat(acc) ? cur : acc), outpoints[0]);
				this.appTrackMinInpoint = (typeof minInpoint === 'string' && minInpoint.indexOf('%') > 0)
					? (parseFloat(minInpoint) / 100) * this.duration
					: parseFloat(minInpoint);
				this.appTrackMaxOutpoint = (typeof maxOutpoint === 'string' && maxOutpoint.indexOf('%') > 0)
					? (parseFloat(maxOutpoint) / 100) * this.duration
					: parseFloat(maxOutpoint);
			}
			this.dragDisabled = (this.appTrack) ? true : this.dragDisabled_def;

			this.widget = this.interactivityEditorService.getWidgetById(this.track.widgetId);
			if (this.widget) {
				this.labelControl.setValue(this.widget.label || this.widget.type);

				if (this.widget.type !== WidgetType.App && this.type !== 'trackFromApp') { // apps and their widgets do not have animations
					const animationMode = this.widget.animations && this.widget.animations.length;
					const animationConfigs: any = {};

					Object.keys(this.widget.config).forEach((key: string) => {
						switch (key) {
							case 'xpos':
							case 'ypos':
							case 'alpha':
							case 'background_alpha':
							case 'width':
							case 'height':
								const value: any = this.widget.config[key];
								const calculatedValue = this.widget.calculatedConfig[key];
								const prop: IWidgetProperty = this.interactivityWidgetDefinitionService
									.getWidgetPropertyFromConfig(key, animationMode ? calculatedValue : value);
								animationConfigs[key] = this.interactivityWidgetService.getComponentConfigFromProperty(prop);
								animationConfigs[key].ctype = 'pillbox_animation';
						}
						if (key === 'alpha' || key === 'background_alpha') {
							animationConfigs[key].index = '__alpha__';
						}
					});

					this.animateConfig = [];

					if (this.widget.config.ypos && this.widget.config.xpos) {
						this.animateConfig.push({
							pillboxConfig: [
								animationConfigs.xpos,
								animationConfigs.ypos
							],
							icon: 'circle',
							timepoints: this.widget.animations.find(item => item && item.legend === 'xpos,ypos')
						});
					}

					if (this.widget.config.alpha || this.widget.config.background_alpha) {
						const alphaConfig = animationConfigs.alpha || animationConfigs.background_alpha;
						this.animateConfig.push({
							pillboxConfig: [
								alphaConfig
							],
							icon: 'info',
							timepoints: this.widget.animations.find(item => item && item.legend === 'alpha')
						});
					}

					if (this.widget.config.width && this.widget.config.height) {
						this.animateConfig.push({
							pillboxConfig: [
								animationConfigs.width,
								animationConfigs.height
							],
							icon: 'link',
							timepoints: this.widget.animations.find(item => item && item.legend === 'width,height')
						});
					}
				}
			}
		} else { // trackgroup
			if (this.track && (this.track instanceof TrackGroup)) {
				this.labelControl.setValue(this.track.label);
				this.visibleStageWidgets = [];
				this.stageWidgets = [];

				if (this.track && this.track.tracks && this.track.tracks.length) {
					this.track.tracks.forEach((track) => {
						if (track && track.widgetId) {
							const widget = this.interactivityEditorService.getWidgetById(track.widgetId);
							if (widget instanceof StageWidget) {
								this.stageWidgets.push(widget);
								if (widget.visibility === 'visible') {
									this.visibleStageWidgets.push(widget);
								}
							}
						}
					});
				}
			}
		}
		if (this.backgroundCanvas && this.backgroundCanvas.nativeElement) {
			this.interactiveShowHide();
		}
	}

	tooltipMouseDown(ev: MouseEvent) {
		ev.stopPropagation();
	}

	ngAfterViewInit() {
		this.durationSubject.subscribe(() => {
			this.interactiveShowHide();
		});
	}

	ngOnDestroy() {
		this.isDestroyed = true;
		if (this.subscriptions) {
			this.subscriptions.unsubscribe();
		}
	}

	interactiveShowHide() {
		if (!this.duration || !this.widget || !this.backgroundCanvas?.nativeElement) {
			return;
		}
		const ignoreTimelineShow = this.widget.getConfigValue('ignoreTimelineShow') === 'true';
		const ignoreTimelineHide = this.widget.getConfigValue('ignoreTimelineHide') === 'true';

		let width = this.backgroundCanvas.nativeElement.offsetWidth;
		this.backgroundCanvas.nativeElement.width = width;
		const height = this.backgroundCanvas.nativeElement.height;
		this.backgroundCanvas.nativeElement.height = height;

		const context: CanvasRenderingContext2D = this.backgroundCanvas.nativeElement.getContext('2d');
		let x1: number;
		let y1: number;
		const delta = 7;

		let offset: number;

		if (ignoreTimelineShow && ignoreTimelineHide) {
			x1 = 0;
			y1 = 0;
			width = (width - x1) / this.zoomFactor;
		} else if (ignoreTimelineShow || ignoreTimelineHide) {

			this.track.inOutPoints.map((currentInOutPoint: IInOutPoint) => {
				const inpoint: number = parseFloat(currentInOutPoint.inpoint);
				offset = (inpoint + ((parseFloat(currentInOutPoint.outpoint) - inpoint) / 2)) / this.duration;
			});

			if (offset >= 0 && offset <= 1) {
				if (ignoreTimelineShow) {
					x1 = 0;
					y1 = 0;
					width = width * offset;
				} else {
					x1 = offset * width;
					y1 = 0;
					width = width - x1;
				}
			}
		} else {
			context.clearRect(0, 0, width, height);
		}

		context.rect(x1, y1, width, height);
		context.strokeStyle = this.selectedTrackIds.indexOf(this.track.id) === -1 ? BACKGROUND_COLOR : IE_NEUTRAL;

		try {
			const lineArray = Array(Math.ceil((width - (-1 * width)) / delta)).fill(-1 * width).map((x, y) => x + y * delta);
			context.save();
			context.clip();
			lineArray.forEach(n => {
				context.beginPath();
				context.moveTo(n + x1, y1);
				context.lineTo(height + n + x1, y1 + height);
				context.stroke();
			});
			context.restore();
		} catch (e) {

		}
	}

	onTrackMouseOver(track: ITrackAreaItem, ev: MouseEvent) {
		if (track instanceof Track) {
			if (track && track.widgetId) {
				this.widgetSelectionService.setHoveredWidgets([track.widgetId]);
			}
		} else if (track && track instanceof TrackGroup && track.tracks) {
			this.widgetSelectionService.setHoveredWidgets(track.tracks.map(trackInGroup => trackInGroup.widgetId));
			this.groupHovered = true;
			this.stateChanges.next();
		}
	}

	onTrackMouseLeave() {
		this.widgetSelectionService.clearHoveredWidgets();
		this.groupHovered = false;
		this.stateChanges.next();
	}

	isTrackHovered(track: ITrackAreaItem) {
		return (track && track.widgetId && this.hoveredWidgetIds.indexOf(track.widgetId) !== -1);
	}

	onInOutPointClick(event: MouseEvent, inOutPoint: IInOutPoint): void {
		// Only clear selected widgets when no meta key is used
		if (!modKeyDown(event)) {
			this.widgetSelectionService.clearSelectedWidgets();
			this.widgetSelectionService.addSelectedWidgetById(this.track.widgetId, { area: 'tracks' });
		} else {
			if (this.widgetSelectionService.selectedWidgetIds) {
				const selectedWidgets = this.widgetSelectionService.selectedWidgetIds.filter((widgetId: string) => widgetId === this.track.widgetId);
				if (selectedWidgets.length > 0) {
					this.widgetSelectionService.removeSelectedWidgetById(this.track.widgetId);
				} else {
					this.widgetSelectionService.addSelectedWidgetById(this.track.widgetId, { area: 'tracks' });
				}
			}
		}
	}

	getIOPointStyles(ioPoint: IInOutPoint): any {
		if (this.duration) {
			let offset = 0;
			let duration = this.duration;
			if (this.appTrack && this.appTrack.inOutPoints) {
				offset = this.appTrackMinInpoint;
				duration = this.appTrackMaxOutpoint - this.appTrackMinInpoint;
			}

			if (ioPoint && !isNaN(parseFloat(ioPoint.inpoint)) && !isNaN(parseFloat(ioPoint.outpoint))) {
				let inPoint: number = parseFloat(ioPoint.inpoint);
				if (typeof ioPoint.inpoint === 'string' && ioPoint.inpoint.indexOf('%') > 0) {
					inPoint = ((inPoint / 100) * duration) + offset;
				} else if (this.appTrack && this.appTrack.inOutPoints && inPoint === 0) { // hack for new-style apps
					inPoint = offset;
				}
				let outPoint: number = parseFloat(ioPoint.outpoint);
				if (typeof ioPoint.outpoint === 'string' && ioPoint.outpoint.indexOf('%') > 0) {
					outPoint = ((outPoint / 100) * duration) + offset;
				} else if (this.appTrack && this.appTrack.inOutPoints && outPoint === 100) { // hack for new-style apps
					outPoint = duration + offset;
				}

				this.rightData = outPoint / this.duration * this.viewPortWidth;
				const left = (inPoint / this.duration * 100);
				const trackDuration = (outPoint - inPoint);

				const width = trackDuration / this.duration * 100;

				return {
					left: left + '%',
					width: width + '%',
					visibility: 'visible'
				};
			}
		} else {
			return {};
		}
	}

	get viewPortWidth(): number {
		if (this.trackElementContainer) {
			return +this.trackElementContainer.nativeElement.offsetWidth;
		}
	}

	get viewPortDuration(): number {
		return this.duration; // for now
	}

	tooltipLeftOffset(inpoint: string) {
		const left: string = (typeof inpoint === 'string' && inpoint.indexOf('%') > 0) ? inpoint : ((parseFloat(inpoint) || 0) / this.duration * 100) + '%';
		return left;
	}

	tooltipRightOffset(outpoint: string) {
		const left: string = (typeof outpoint === 'string' && outpoint.indexOf('%') > 0) ? outpoint : ((parseFloat(outpoint) || 0) / this.duration * 100) + '%';
		if (this.viewPortWidth) {
			this.hideRightData = this.viewPortWidth - 41;
		}
		if (this.rightData > this.hideRightData) {
			return {
				'z-index': '7',
				left: 'calc(' + left + '- 70px)'
			};
		} else {
			return {
				left
			};
		}
	}

	onTrackElementMouseDown(ev: MouseEvent) {
		ev.stopPropagation();
	}

	onTrackElementDragStarted(ioPoint: IInOutPoint, ev: BbDragEvent) {
		ev.source.event.stopPropagation();
		this.canvasVisible = false;
		this.trackElementDragging = true;
		this.setTrackSnapPoints(this.tracks);
	}

	onTrackResizeStart() {
		// this.canvasVisible = false;
		// this.trackElementDragging = true;
		this.setTrackSnapPoints(this.tracks);
	}

	onTrackResizeEvent(ioPoint: IInOutPoint, ev: BbResizeEvent) {
		const viewPortWidth: number = this.viewPortWidth;
		const viewPortDuration: number = this.viewPortDuration;
		const viewPortPixelsPerSecond = (viewPortDuration > 0) ? viewPortWidth / viewPortDuration : 1.0;
		// let newIoPoint = ioPoint;
		const newIoPoint = { ...ioPoint };

		if (ev.direction.includes('w')) {
			newIoPoint.inpoint = ev.snappedX && ev.snapValues && ev.snapValues.x0 ? ev.snapValues.x0 : (ev.left / viewPortPixelsPerSecond).toFixed(3);
		} else if (ev.direction.includes('e')) {
			newIoPoint.outpoint = ev.snappedX && ev.snapValues && ev.snapValues.x1
				? ev.snapValues.x1
				: ((ev.left + ev.width) / viewPortPixelsPerSecond).toFixed(3);
		}
		this.leftData = parseFloat(newIoPoint.inpoint) / this.duration * this.viewPortWidth;
		this.rightData = parseFloat(newIoPoint.outpoint) / this.duration * this.viewPortWidth;
		if (ev.type === 'resized') {
			(ioPoint as any).inpointTMP = newIoPoint.inpoint;
			(ioPoint as any).outpointTMP = newIoPoint.outpoint;
		} else if (ev.type === 'resizeended') {
			delete (ioPoint as any).inpointTMP;
			delete (ioPoint as any).outpointTMP;
			this.canvasVisible = true;
			this.trackElementDragging = false;
			this.dispatchChange(ioPoint, newIoPoint.inpoint, newIoPoint.outpoint);
		}
	}

	onTrackElementDragEnd(ioPoint: IInOutPoint, ev: BbDragEvent) {
		this.canvasVisible = true;
		this.trackElementDragging = false;

		const offset = ev.offset;

		if (offset.x === 0 && offset.y === 0) {
			return;
		}

		const update = { ...ioPoint };
		const duration = parseFloat(update.outpoint) - parseFloat(update.inpoint);
		let leftData = parseFloat(update.inpoint) / this.duration * this.viewPortWidth;
		let rightData = parseFloat(update.outpoint) / this.duration * this.viewPortWidth;
		let snapped = false;
		if (ev.snappedX && ev.snapValues && ev.snapValues.x0) {
			update.inpoint = ev.snapValues.x0;
			update.outpoint = String(parseFloat(update.inpoint) + duration);
			leftData = parseFloat(update.inpoint) / this.duration * this.viewPortWidth;
			rightData = parseFloat(update.outpoint) / this.duration * this.viewPortWidth;
			// offset.x = leftData - (parseFloat(ioPoint.inpoint) / this.duration * this.viewPortWidth);
			snapped = true;
		}
		if (ev.snappedX && ev.snapValues && ev.snapValues.x1) {
			update.outpoint = ev.snapValues.x1;
			update.inpoint = String(parseFloat(update.outpoint) - duration);
			leftData = parseFloat(update.inpoint) / this.duration * this.viewPortWidth;
			rightData = parseFloat(update.outpoint) / this.duration * this.viewPortWidth;
			// offset.x = leftData - (parseFloat(ioPoint.inpoint) / this.duration * this.viewPortWidth);
			snapped = true;
		}

		if (!snapped) {
			this.updateIOPoint(update, offset);
		}
		this.leftData = leftData;
		this.rightData = rightData;
		this.dispatchChange(ioPoint, update.inpoint, update.outpoint);
	}

	setTrackSnapPoints(trackContainers: FlatITrackAreaItem[]) {
		this.snappingTargets = [];
		const containerWidth = this.trackElementContainer.nativeElement.offsetWidth;
		trackContainers.forEach((trackContainer) => {
			if (trackContainer && trackContainer.type !== 'trackFromApp' && trackContainer.child && trackContainer.child.elRef) {
				const trackEl = trackContainer.child.elRef.querySelector('track-element') as HTMLElement;
				if (trackEl) {
					if (trackContainer.child.inOutPoints && trackContainer.child.inOutPoints.length) {
						this.addElementSnappingPoints(trackEl, trackContainer.child.inOutPoints[0]);
					}
				}
			}
		});

		// find time needle and also snap to current time:
		const timelineEl = this.track.elRef && this.track.elRef.closest('timeline') as HTMLElement;
		const timeIndicator = timelineEl && timelineEl.querySelector('.timeline-indicator-body') as HTMLElement;
		if (timeIndicator) {
			this.snappingTargets.push({
				x: timeIndicator.offsetLeft,
				xValue: this.interactivityMediaService.currentTime
			});
		} else {
			this.snappingTargets.push({
				x: ((this.interactivityMediaService.currentTime || 0) / this.duration) * containerWidth,
				xValue: this.interactivityMediaService.currentTime
			});
		}
	}

	addElementSnappingPoints(el: HTMLElement, ioPoint: IInOutPoint) {
		const rect = el.getBoundingClientRect();
		const props = {
			x: el.offsetLeft,
			y: el.offsetTop,
			width: rect.width,
			height: rect.height
		};

		this.snappingTargets = this.snappingTargets.concat([
			{
				x: props.x,
				xValue: ioPoint.inpoint
			}, {
				x: props.x + props.width,
				xValue: ioPoint.outpoint
			}
		]);
	}

	changeInOutPoint(ioPoint: IInOutPoint, inPoint: string, outPoint: string, from?: 'in' | 'out') {
		let leftSide: number = Math.min(parseFloat(inPoint), this.duration);
		if (leftSide === this.duration) {
			leftSide = this.duration - 2;
		}
		let rightSide: number = Math.min(parseFloat(outPoint), this.duration);
		if (leftSide >= rightSide) {
			if (from === 'in') {
				rightSide = Math.min(leftSide + 5, this.duration);
			} else {
				leftSide = Math.max(rightSide - 5, 0);
			}
		}

		const leftData = leftSide / this.duration * this.viewPortWidth;
		const rightData = rightSide / this.duration * this.viewPortWidth;
		if (this.previousLeftData !== leftData || this.previousRightData !== rightData) {
			this.leftData = this.previousLeftData = leftData;
			this.rightData = this.previousRightData = rightData;
			this.dispatchChange(ioPoint, String(leftSide), String(rightSide));
		}
	}

	onInputWrapperClicked() {
		if (this.track instanceof Track) {
			this.widgetSelectionService.clearSelectedWidgets();
			this.widgetSelectionService.addSelectedWidgetById(this.widget.id, { area: 'tracks' });
		}
		this.inputRef.nativeElement.focus();
	}

	onLabelInputChange(ev: Event, newLabel: string) {
		ev.stopPropagation();
		ev.stopImmediatePropagation();
		ev.preventDefault();
		ev.cancelBubble = true;
		setTimeout(() => {
			if (this.track instanceof Track) {
				this.interactivityEditorService.changeWidgetLabel(this.widget.id, newLabel);
			} else {
				this.interactivityEditorService.changeGroupLabel(this.track, newLabel);
			}
			this.inputRef.nativeElement.blur();
		});
	}

	onLabelInputKeyDown(evt: KeyboardEvent, newLabel: string) {
		if (!this.widget) {
			return;
		}
		if (evt.key === 'Escape') {
			evt.preventDefault();
			this.labelControl.setValue((this.widget as any)._data.label || (this.widget as any)._data.widgetdefinitiontype);
			this.inputRef.nativeElement.blur();
		}
		if (evt.key === 'Enter' || evt.key === 'Tab') {
			if (newLabel === (this.widget as any)._data.label || newLabel === (this.widget as any)._data.widgetdefinitiontype) {
				evt.preventDefault();
				setTimeout(() => {
					this.inputRef.nativeElement.blur();
				});
			} else if (newLabel === '') {
				this.labelControl.setValue((this.widget as any)._data.widgetdefinitiontype);
			}
		}
	}

	// For now, dragging is disabled when editing the label, because cdkDragHandle cannot be placed inside a child component.
	// See https://github.com/angular/material2/issues/13784.
	// Todo: place drag handler in tracks component so that only the label is draggable.
	onFocusLabelInput() {
		this.onEditTrack.emit(true);
	}

	onBlurLabelInput() {
		this.onEditTrack.emit(false);
	}

	onUpdateIgnoreShow(value: boolean) {
		this.reduxService.dispatch({
			type: TimelineDataActions.CHANGE_WIDGET_CONFIG,
			id: this.widget.id,
			config: { ...this.widget.config, ...{ ignoreTimelineShow: value ? 'true' : 'false' } },
			stateSet: this.interactivityEditorService.activeStateSet,
			state: this.interactivityEditorService.activeState,
			description: 'Changed ignore show of widget: ' + value
		});
	}

	onUpdateIgnoreHide(value: boolean) {
		this.reduxService.dispatch({
			type: TimelineDataActions.CHANGE_WIDGET_CONFIG,
			id: this.widget.id,
			config: { ...this.widget.config, ...{ ignoreTimelineHide: value ? 'true' : 'false' } },
			stateSet: this.interactivityEditorService.activeStateSet,
			state: this.interactivityEditorService.activeState,
			description: 'Changed ignore hide of widget: ' + value
		});
	}

	onTrackElementContextMenu(ev: Event): void {
		if (ev) {
			ev.preventDefault();
			ev.stopPropagation();
		}

		this.trackElementContextMenu.emit({ ev, item: this.track, label: this.labelControl.value }); // ignore ioPoint for now
	}

	private dispatchChange(ioPoint: IInOutPoint, inPoint: string, outPoint: string): void {
		this.interactivityEditorService.updateInOutPoint(this.track.id, ioPoint, inPoint, outPoint);
	}

	private updateIOPoint(ioPoint: IInOutPoint, offset: any) {
		const viewPortWidth: number = this.viewPortWidth;
		const viewPortDuration: number = this.viewPortDuration;
		const viewPortPixelsPerSecond = (viewPortDuration > 0) ? viewPortWidth / viewPortDuration : 1.0;

		// both
		let inpoint_num = parseFloat(ioPoint.inpoint) + (offset.x / viewPortPixelsPerSecond);
		let outpoint_num = parseFloat(ioPoint.outpoint) + (offset.x / viewPortPixelsPerSecond);
		inpoint_num = Math.min(Math.max(0, inpoint_num), outpoint_num);
		outpoint_num = Math.min(Math.max(inpoint_num, outpoint_num), viewPortDuration);
		ioPoint.inpoint = (+inpoint_num.toFixed(3)).toString();
		ioPoint.outpoint = (+outpoint_num.toFixed(3)).toString();
	}

}
